import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { Observable, take } from 'rxjs';

import * as fileSaver from 'file-saver';

import { ApiService, ApplicationSettings, BreadcrumbService, HeaderService, SpinnerService, UserService } from 'dspx-core';

import { APP_DATA_KEYS, APPLICATION, BREADCRUMBS } from '../constants/app-data.constants';
import { ApiBaseRoute } from '../constants/api-endpoints.constants';
import { LogLevel } from '../constants/log.constants';

import { ExportRequest } from '../interfaces/export-request.interface';

import { DateTimeHelper } from '../helpers/date-time-helper';

import { SessionStorageService } from './session-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AppDataService extends ApiService {

  appData: { [key: string]: any } = {};

  headerConfig: any;

  constructor(
    protected httpClient: HttpClient,
    private appSettings: ApplicationSettings,
    private spinnerService: SpinnerService,
    private userService: UserService,
    private headerService: HeaderService,
    private breadcrumbService: BreadcrumbService,
    private titleService: Title,
    private sessionStorageService: SessionStorageService) {

    super(httpClient, appSettings.apiUrl + ApiBaseRoute);

    this.setAppData(APP_DATA_KEYS.logLevel, LogLevel.info);
  }

  public setAppData<TValue>(key: string, value: TValue) {
    this.appData[key] = value;
  }

  public getAppData<TValue>(key: string): TValue {
    return this.appData[key] as TValue;
  }

  public getFromSessionStorage<TKey, TValue>(key: TKey): TValue {
    return this.sessionStorageService.get<TKey, TValue>(key);
  }

  public setIntoSessionStorage<TKey, TValue>(key: TKey, value: TValue): void {
    this.sessionStorageService.set<TKey, TValue>(key, value);
  }

  public generatePdf(relativeUrl: string, downloadFile = true) {
    const url = this.getUrl(relativeUrl);

    this.httpClient
      .post(url, null, { responseType: 'blob', observe: 'response' })
      .subscribe({
        next: response => {
          const dispositionHeader = response.headers.get('content-disposition');
          if (!dispositionHeader) {
            throw new Error('content-disposition header is not found');
          }
          if (downloadFile === true) {
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.download = dispositionHeader.split(';')[1].trim().split('=')[1];
            a.href = URL.createObjectURL(response.body);
            a.target = '_blank';
            a.click();
            document.body.removeChild(a);
          }
          else {
            const fileURL = URL.createObjectURL(response.body);
            window.open(fileURL, 'PdfTabWindow');
          }
        },
        error: err => {
          throw err;
        }
      });
  }

  public delete(endpoint: string): Observable<any> {
    return super.delete(endpoint);
  }

  public post<T>(endpoint: string, request: T): Observable<any> {
    return this.postAndFetch<T>(endpoint, request);
  }

  public fetchData(endpoint: string): Observable<any> {
    return this.postAndFetch<any>(endpoint, {});
  }

  public getData<T>(endpoint: string, queryParams?: Record<string, any>): Observable<T> {
    const params = this.toHttpParams(queryParams);
    return this.get<T>(endpoint, { params });
  }

  public getCsv(relativeUrl: string, requestBody: any, defaultFileName: string) {
    const url = this.getUrl(relativeUrl);

    this.spinnerService.showSpinner();
    this.httpClient
      .post(url, requestBody, { responseType: 'blob', observe: 'response' })
      .subscribe({
        next: response => {
          this.spinnerService.hideSpinner();
          let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          const data: Blob = new Blob([response.body], {
            type: EXCEL_TYPE
          });
          fileSaver.saveAs(data, defaultFileName + '_' + DateTimeHelper.now().toJSDate().getTime() + '.csv');
        },
        error: err => {
          throw err;
        }
      });
  }

  public getDataTableSet<TResponse>(apiEndpoint: string, request: ExportRequest, onSuccess: (response: TResponse, transientId: string) => void): void {
    this.post(apiEndpoint, request)
      .pipe(take(1))
      .subscribe((response: TResponse) => onSuccess(response, this.getTransientId()));
  }

  public getTransientId(): string {
    return `${new Date().getTime()}`;
  }

  public getAttributeFromHtmlElement(elementId: string, attributeName: string): string {
    return document.getElementById(elementId)?.attributes[attributeName]?.value;
  }

  public getValueAttributeFromHtmlElement(elementId: string): string {
    return this.getAttributeFromHtmlElement(elementId, 'value');
  }

  public getUsersInitials(name: string) {
    // Split name property by space delimiter
    const wordsArray = (name && name.split(' ')) || '';
    let initials = '';
    const firstInitial = wordsArray[0].charAt(0);
    let lastInitial = '';
    // If more than one word in name property
    if (wordsArray.length > 1) {
      // If the last character is not a number or special character we can use it
      if (/^[a-zA-Z ,_]*$/.test(wordsArray[wordsArray.length - 1].charAt(0)) === true) {
        lastInitial = wordsArray[wordsArray.length - 1].charAt(0);
        initials = firstInitial.toUpperCase() + lastInitial.toUpperCase();
      } else { // Else only use the inital from the first name.
        initials = firstInitial.toUpperCase();
      }
    } else {
      initials = firstInitial.toUpperCase();
    }
    return initials;
  }

  public getUserDetails() {
    // Get Logged In User and update header
    this.userService
      .getLoggedInUser('getLoggedInUser')
      .pipe(take(1))
      .subscribe((userProfile) => {
        this.headerConfig = {
          applicationTitle: {
            label: APPLICATION.title,
          },
          applicationLogo: {
            source: APPLICATION.logo,
          },
          loggedInUser: {
            name: userProfile.fullName,
            imageSource: '/assets/images/avatar.png',
            imageDescription: userProfile.fullName,
            menuSubItems: [
              {
                label: 'Logout',
                icon: 'pi pi-fw pi-sign-out',
                routerLink: 'logout',
              },
            ],
          },
        };
        this.headerService.onHeaderUpdated(this.headerConfig);
      });
  }

  // The DSPX framework sets the HTML title based on the application title, the application sub-title, the form name, the current navigation tab, and the current page.
  // In this application we need to set the HTML title based only on the application title and the form name, so we override the value set by the DSPX framework.
  public overrideHtmlTitleSetByDspxFramework(formName: string): void {
    this.titleService.setTitle(`${APPLICATION.title} - ${formName}`);
  }

  public setBreadcrumbs(items: { label: string, url?: string }[]): void {
    const breadcrumbMenuItem = items?.map(item => ({ separatorIcon: BREADCRUMBS.separatorIcon , ...item })) ?? [];
    this.breadcrumbService.setCrumbs(breadcrumbMenuItem);
  }

  private toHttpParams(record: Record<string, any>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(record ?? {})) {
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v !== undefined && v !== null) {
            params = params.append(key, v.toString());
          }
        });
      }
      else if (value !== undefined && value !== null) {
        params = params.set(key, value.toString());
      }
    }
    return params;
  }
}
